import {IHotspotData} from "./IHotspotData";
import {CoreAssets} from "../core/CoreAssets";

/******************************************************************
 * HotspotsData
 *
 * @author matthias.schulz@jash.de
 *****************************************************************/

export class HotspotsData {

    static readonly items: IHotspotData[] = [
        {
            id: "food",
            illustrationSVG: CoreAssets.FOOD_HOTSPOT,
            scale: 1.6,
            selectedScale: 1.6,
            position: {
                x: 0.88,
                y: 0.3
            },
            copy: {
                title: {
                    en: "Food: Reduce",
                    de: "Lebensmittel: Reduzieren"
                },
                desc: {
                    en: "<p>Food waste is a global problem. In Australia alone, 7.6 million metric tons of food go to waste annually in the commercial sector.</p><p><span class='arrow'></span><strong>Yüme</strong>, based in South Melbourne, Australia, is a business-to-business online marketplace for quality surplus products that connects suppliers with buyers. It describes itself as the eBay of surplus food.</p>",
                    de: "<p>Lebensmittelverschwendung ist ein globales Problem. Allein in Australien werden jedes Jahr im gewerblichen Sektor 7,6 Millionen Tonnen Lebens&shy;mittel entsorgt.</p><p><span class='arrow'></span><strong>Yüme</strong> aus South Mel&shy;bourne/Austra&shy;lien ist ein Online-Marktplatz für Firmenkunden zum Austausch hochwertiger Restposten. Das Unternehmen bezeichnet sich als eBay für überschüssige Lebens&shy;mittel.</p>"
                },
                factTitle: {
                    en: "1/3",
                    de: "1/3"
                },
                factDesc: {
                    en: "<p>of the <strong>food</strong> produced worldwide for human consumption every year – approximately 1.3 billion metric tons – is lost or wasted.</p>",
                    de: "<p>der <strong>Lebensmittel</strong>, die jedes Jahr weltweit für den menschlichen Verzehr produziert werden, etwa 1,3 Milliarden Tonnen, verderben oder werden verschwendet.</p>"
                },
                factSource: {
                    en: "Source: FAO",
                    de: "Quelle: FAO"
                }
            }
        },
        {
            id: "clothes",
            illustrationSVG: CoreAssets.CLOTHING_HOTSPOT,
            scale: 1,
            selectedScale: 1.1,
            position: {
                x: 0.92,
                y: 0.6
            },
            copy: {
                title: {
                    en: "Clothing: Reuse",
                    de: "Kleidung: Wiederverwenden"
                },
                desc: {
                    en: "<p>Second-hand is increasingly fashionable. New platforms connect buyers with sellers, keeping clothing in use and out of landfill for longer.</p><p><span class='arrow'></span><strong>thredUP</strong>, based in Oakland, United States, is one of several recommerce platforms aiming to meet this demand at scale with smart algorithms to determine prices. It can process some 111,000 items a day.</p>",
                    de: "<p>Secondhand kommt immer mehr in Mode. Neue Plattformen bringen Käufer und Verkäufer zusammen und sorgen dafür, dass Kleidung länger getragen und nicht weggeworfen wird.</p><p><span class='arrow'></span><strong>thredUP</strong> aus Oakland/USA ist eine von mehreren Wieder&shy;ver&shy;kaufs&shy;platt&shy;formen, die diese Nachfrage mit intelligenten Algorithmen zur Preis&shy;ermit&shy;tlung bedienen möchte. Täglich werden rund 111.000 Artikel verarbeitet.</p>"
                },
                factTitle: {
                    en: "64%",
                    de: "64%"
                },
                factDesc: {
                    en: "<p>of the 32 billion <strong>garments</strong> produced annually in the United States end up in landfill.</p>",
                    de: "<p>der 32 Milliarden <strong>Kleidungsstücke</strong>, die jedes Jahr in den USA hergestellt werden, landen auf der Mülldeponie.</p>"
                },
                factSource: {
                    en: "Source: thredUP",
                    de: "Quelle: thredUP"
                }
            }
        },
        {
            id: "health",
            illustrationSVG: CoreAssets.HEALTH_HOTSPOT,
            scale: 1.25,
            selectedScale: 1.2,
            position: {
                x: 0.87,
                y: 0.85
            },
            copy: {
                title: {
                    en: "Health care: Refurbish",
                    de: "Gesundheit: Wiederaufbereiten"
                },
                desc: {
                    en: "<p>Medical equipment is too valuable to end up on a rubbish heap. It contains many complex and resource-intensive components.</p><p><span class='arrow'></span>Health technology company <strong>Philips</strong>, based in Amsterdam, Netherlands, refurbishes used medical equipment such as Magnetic Resonance Imaging Systems to the same quality and performance as new, for resale.</p>",
                    de: "<p>Medizinische Geräte sind zu wertvoll für den Müll, denn sie enthalten viele komplexe und ressourcenintensive Komponenten.</p><p><span class='arrow'></span>Das Gesundheits&shy;techno&shy;logie&shy;unter&shy;nehmen <strong>Philips</strong> aus Amster&shy;dam/Nie&shy;der&shy;lande bereitet gebrauchte medizinische Geräte wie Magnet&shy;re&shy;sonanz&shy;tomo&shy;grafen für den Wieder&shy;ver&shy;kauf auf. Sie haben die gleiche Qualität und Leistung wie neue Geräte.</p>"
                },
                factTitle: {
                    en: "4.6%",
                    de: "4,6 %"
                },
                factDesc: {
                    en: "<p>of worldwide <strong>greenhouse gas emissions</strong> are produced by the <strong>health care industry.</strong></p>",
                    de: "<p>der weltweiten <strong>Treibhausgasemissionen</strong> werden vom <strong>Gesundheitswesen</strong> verursacht.</p>"
                },
                factSource: {
                    en: "Source: Health Care Without Harm",
                    de: "Quelle: Health Care Without Harm"
                }
            }
        },
        {
            id: "housing",
            illustrationSVG: CoreAssets.HOUSE_HOTSPOT,
            scale: 1.2,
            selectedScale: 1.2,
            position: {
                x: 0.52,
                y: 1.05
            },
            copy: {
                title: {
                    en: "Housing: Renewables",
                    de: "Wohnen: Nachwachsende Rohstoffe"
                },
                desc: {
                    en: "<p>Architects and builders are increasingly looking to alternative materials for building construction to reduce the carbon footprint of our homes and offices.</p><p><span class='arrow'></span>Biotech firm <strong>Biohm</strong>, based in London, England, has come up with an alternative insulation material made from mushrooms. Part of their roots are combined with synthetic by-products from industry to create insulation panels.</p>",
                    de: "<p>Architekten und Bauherren suchen zunehmend nach alternativen Ma&shy;teri&shy;alien für den Bau von Gebäuden, um deren CO₂-Fußabdruck zu ver&shy;ring&shy;ern.</p><p><span class='arrow'></span>Das Biotech-Unternehmen <strong>Biohm</strong> mit Sitz in London/England hat einen alternativen Dämmstoff aus Pilzen entwickelt. Aus Teilen der Pilzwurzel und synthetischen Neben&shy;produkten der Industrie werden Dämmplatten hergestellt.<p/>"
                },
                factTitle: {
                    en: "40%",
                    de: "40%"
                },
                factDesc: {
                    en: "<p>of total direct and indirect <strong>CO₂ emissions</strong> comes from the buildings and <strong>building construction</strong> sectors.</p>",
                    de: "<p>der gesamten direkten und indirekten <strong>CO₂-Emissionen</strong> stammen von <strong>Gebäuden und deren Bau.</strong></p>"
                },
                factSource: {
                    en: "Source: IEA",
                    de: "Quelle: IEA"
                }
            }
        },
        {
            id: "electronic",
            illustrationSVG: CoreAssets.ELECTRONIC_HOTSPOT,
            scale: 1.2,
            selectedScale: 1.3,
            position: {
                x: 0.17,
                y: 0.92
            },
            copy: {
                title: {
                    en: "Electronic: Repair",
                    de: "Elektronik: Reparieren"
                },
                desc: {
                    en: "<p>Today, most electronic products are black boxes, sealed and inaccessible. Gone are the days when people could fix their transistor radio at home.</p><p><span class='arrow'></span>Now those skills are making a comeback. <strong>Framework Computer</strong> in San Francisco, United States, has designed a laptop that users can repair, configure and upgrade themselves.</p>",
                    de: "<p>Heute sind die meisten elektronischen Produkte versiegelt und unzugänglich. Vorbei sind die Zeiten, in denen man sein Transistorradio zu Hause reparieren konnte.</p><p><span class='arrow'></span>Doch jetzt erlebt das Handwerk ein Comeback. <strong>Framework Computer</strong> in San Francisco/USA hat einen Laptop entwickelt, den die Benutzer selbst reparieren, konfigurieren und aufrüsten können.</p>"
                },
                factTitle: {
                    en: "57.4",
                    de: "57,4"
                },
                factDesc: {
                    en: "<p><strong>million metric tons</strong> of <strong>e-waste</strong> is estimated to be generated globally in 2021.</p>",
                    de: "<p><strong>Millionen Tonnen Elektroschrott</strong> werden schätzungsweise im Jahr 2021 weltweit anfallen.</p>"
                },
                factSource: {
                    en: "Source: Waste Electrical and Electronic Equipment Forum",
                    de: "Quelle: Waste Electrical and Electronic Equipment Forum"
                }
            }
        },
        {
            id: "mobility",
            illustrationSVG: CoreAssets.MOBILITY_HOTSPOT,
            scale: 1.4,
            selectedScale: 1.4,
            position: {
                x: 0.12,
                y: 0.58
            },
            copy: {
                title: {
                    en: "Mobility: Share",
                    de: "Mobilität: Teilen"
                },
                desc: {
                    en: "<p>The use of shared e-bikes and e-scooters in many cities is increasing, but the environmental benefits can be impacted by the need to collect them by van for recharging.</p><p><span class='arrow'></span>Bike-sharing company <strong>Hello Inc.</strong> in Shanghai, China, deploys over one million electric two-wheelers. Together with Chinese battery-maker CATL, it has created a kiosk network where users can swap depleted batteries.</p>",
                    de: "<p>E-Bikes und E-Scooter erobern die Straßen. Der Nutzen für die Umwelt ist begrenzt, wenn sie zum Aufladen mit einem Lieferwagen eingesammelt werden müssen.</p><p><span class='arrow'></span><strong>Hello Inc.</strong> aus Schanghai/China hat über eine Million E-Bikes in Betrieb. Gemeinsam mit dem chinesischen Batteriehersteller CATL hat die Firma ein Netzwerk an Stationen aufgebaut, an denen Nutzer leere Batterien tauschen können.</p>"
                },
                factTitle: {
                    en: "49%",
                    de: "49%"
                },
                factDesc: {
                    en: "<p>lower lifecycle <strong>greenhouse gas emissions</strong> per passenger kilometer on average for 4 <strong>shared e-bikes</strong> compared to conventional private cars.</p>",
                    de: "<p>weniger <strong>Treibhausgasemissionen</strong> pro Personenkilometer fallen durchschnittlich im Lebenszyklus eines <strong>gemeinsam genutzten E-Bikes</strong> im Vergleich zu einem konventionellen Privat-Pkw an.</p>"
                },
                factSource: {
                    en: "Source: International Transport Forum",
                    de: "Quelle: Weltverkehrsforum"
                }
            }
        },
        {
            id: "recycling",
            illustrationSVG: CoreAssets.RECYCLING_HOTSPOT,
            scale: 1.2,
            selectedScale: 1.4,
            position: {
                x: 0.171,
                y: 0.26
            },
            copy: {
                title: {
                    en: "End of life: Recycling",
                    de: "Lebensende: Recyceln"
                },
                desc: {
                    en: "<p>What about the end of our own life? The wood and cement used for burial and the energy for cremation have a substantial environmental impact.</p><p><span class='arrow'></span><strong>Recompose</strong> in Seattle, United States, has developed a process using naturally occurring beneficial microbes that break down the body in weeks. The resulting nutrient-dense soil can be used to enrich forests or gardens.</p>",
                    de: "<p>Was ist mit dem Ende unseres eigenen Lebens? Das für die Bestattung verwendete Holz und der Zement sowie die Energie für Einäscherungen haben erhebliche Auswirkungen auf die Umwelt.</p><p><span class='arrow'></span><strong>Recompose</strong> aus Seattle/USA hat ein Verfahren entwickelt, bei dem natürliche Mikroorganismen den Körper innerhalb von Wochen zersetzen. Die entstehende Erde kann etwa in Gärten verwendet werden.</p>"
                },
                factTitle: {
                    en: "190",
                    de: "190"
                },
                factDesc: {
                    en: "<p><strong>kilograms of CO₂</strong> are emitted for a single <strong>cremation</strong>, depending on the fuel used.</p>",
                    de: "<p><strong>Kilogramm CO₂</strong> werden bei einer einzigen <strong>Einäscherung</strong> freigesetzt, abhängig vom verwendeten Brennstoff.</p>"
                },
                factSource: {
                    en: "Source: Chemical and Engineering News",
                    de: "Quelle: Chemical and Engineering News"
                }
            }
        },
        {
            id: "cloud",
            illustrationSVG: CoreAssets.CLOUD_HOTSPOT,
            scale: 2.4,
            selectedScale: 1.2,
            position: {
                x: 0.53,
                y: -0.05
            },
            copy: {
                title: {
                    en: "Digitalization – backbone of the circular economy",
                    de: "Digitalisierung – das Rückgrat der Kreislaufwirtschaft"
                },
                desc: {
                    en: "<p>Digital technologies drive the circular economy by improving waste-sorting, identifying materials, and working out the best routes for resources to be reused or recycled.</p><p>U.S. retail product cloud developer EON, for example, proposes a digital identity for retail products containing information about the materials they are made of. The data is embedded in the product and goes with it from production to sale, use, reuse and recycling. Starting with the fashion sector, <strong>EON</strong> plans to bring 400 million products online with its CircularID™ Protocol by 2025.</p>",
                    de: "<p>Digitale Technologien unterstützen die Kreislaufwirtschaft, indem sie die besten Wege finden, um Ressourcen wiederzuverwenden und zu recyceln.</p><p>Der US-amerikanische Cloud-Ent&shy;wickler <strong>EON</strong> bietet etwa eine digitale Identität für Einzel&shy;handels&shy;pro&shy;dukte. Die Daten sind in das Produkt integriert und begleiten es von der Herstellung über den Verkauf, die Nutzung und die Wiederverwendung bis zum Recycling. Beginnend mit dem Modesektor plant das Un&shy;ter&shy;nehmen, bis 2025 mit seiner CircularID™ 400 Millionen Produkte online zu stellen.</p>"
                }
            }
        }
    ]
}
